import intelliJetNetService, { QueryBuilder } from '../intellijetnet'
import { AircraftModel } from '../../types/intellijetnet'
import { ensureArray, executeQuery, executeQueryFirst } from './utils'
import { MarketCoverage } from './market-coverage'

export class AircraftModelsService {
  private static readonly TABLE_NAME = 'aircraft_models'

  /**
   * Get query builder for aircraft models with optional market coverage filter
   */
  static query(applyMarketCoverage = true): QueryBuilder {
    const query = intelliJetNetService.from(this.TABLE_NAME)
    
    if (applyMarketCoverage) {
      const makeIds = MarketCoverage.getMakeIds()
      if (makeIds !== null) {
        query.in('make_id', makeIds)
      }
    }
    
    return query
  }

  /**
   * Get model by ID
   */
  static async getById(modelId: number): Promise<AircraftModel | null> {
    return executeQueryFirst<AircraftModel>(
      this.query().eq('model_id', modelId)
    )
  }

  /**
   * Get models by make ID
   */
  static async getByMakeId(makeId: number): Promise<AircraftModel[]> {
    return executeQuery<AircraftModel>(
      this.query()
        .eq('make_id', makeId)
        .eq('is_active', true)
        .order('model')
    )
  }

  /**
   * Get models by weight class
   */
  static async getByWeightClass(weightClass: string): Promise<AircraftModel[]> {
    return executeQuery<AircraftModel>(
      this.query()
        .eq('weight_class', weightClass)
        .eq('is_active', true)
        .order('model')
    )
  }

  /**
   * Get models by category size
   */
  static async getByCategorySize(categorySize: string): Promise<AircraftModel[]> {
    return executeQuery<AircraftModel>(
      this.query()
        .eq('category_size', categorySize)
        .eq('is_active', true)
        .order('model')
    )
  }

  /**
   * Search models by name
   */
  static async searchByName(searchTerm: string): Promise<AircraftModel[]> {
    return executeQuery<AircraftModel>(
      this.query()
        .ilike('model', `%${searchTerm}%`)
        .eq('is_active', true)
        .order('model')
    )
  }

  /**
   * Get models with make information
   */
  static async getWithMakeInfo(modelId?: number): Promise<any[]> {
    let query = this.query()
      .select('*, aircraft_makes(*)')
      .eq('is_active', true)

    if (modelId) {
      query = query.eq('model_id', modelId)
    }

    return executeQuery<any>(
      query.order('model')
    )
  }

  /**
   * Get all weight classes
   */
  static async getWeightClasses(): Promise<string[]> {
    const data = await executeQuery<{ weight_class: string }>(
      intelliJetNetService
        .from(this.TABLE_NAME)
        .select('weight_class')
        .eq('is_active', true)
    )

    const uniqueClasses = [...new Set(data
      .map(item => item.weight_class)
      .filter(Boolean))]
    
    return uniqueClasses.sort()
  }

  /**
   * Get all category sizes
   */
  static async getCategorySizes(): Promise<string[]> {
    const data = await executeQuery<{ category_size: string }>(
      intelliJetNetService
        .from(this.TABLE_NAME)
        .select('category_size')
        .eq('is_active', true)
    )

    const uniqueSizes = [...new Set(data
      .map(item => item.category_size)
      .filter(Boolean))]
    
    return uniqueSizes.sort()
  }

  /**
   * Get model statistics
   */
  static async getStatistics(): Promise<{
    total: number
    byWeightClass: Record<string, number>
    byCategorySize: Record<string, number>
  }> {
    const { count: total } = await this.query()
      .eq('is_active', true)
      .count('exact')
      .execute<AircraftModel>()

    // Get by weight class
    const weightResult = await intelliJetNetService
      .rpc('get_models_by_weight_class_count')
    const weightData = ensureArray<{ weight_class: string; count: number }>(weightResult)

    // Get by category size
    const sizeResult = await intelliJetNetService
      .rpc('get_models_by_category_size_count')
    const sizeData = ensureArray<{ category_size: string; count: number }>(sizeResult)

    return {
      total: total || 0,
      byWeightClass: weightData.reduce((acc: Record<string, number>, item) => {
        acc[item.weight_class] = item.count
        return acc
      }, {}),
      byCategorySize: sizeData.reduce((acc: Record<string, number>, item) => {
        acc[item.category_size] = item.count
        return acc
      }, {})
    }
  }
}

export default AircraftModelsService